/**
 * Tool state helpers for agent chat.
 *
 * This module is intentionally domain-agnostic: it does not know about feature-specific tool names.
 * Embedding features provide an `isDeferredToolPart` classifier (and optionally labels) and platforms
 * render the actual tool UI.
 *
 * Core responsibilities:
 * - Draft-backed question state (`useQuestions`)
 * - Tool submit/cancel wrappers with read-only gating (`canEditSession`)
 * - Pending deferred-tool queue ordering + render-list metadata
 *
 * Cross-platform lifecycle: `packages/core-ui/src/ai/agent/FLOW.md`.
 */
import { useCallback, useMemo } from "react";
import { useZero } from "@corpuscomplete/zero-data-transformer/react";
import { z } from "zod/v4";
import { AgentSession } from "@corpuscomplete/core/ai/agent-session";
import { mutators } from "@corpuscomplete/core/zero";
import { DraftUI } from "../../../common/draft";
import { AgentChat } from "../chat";

export namespace AgentTools {
  export const CUSTOM_OPTION_ID = "__custom__";

  export type Question = {
    id: string;
    label: string;
    options: { id: string; label: string }[];
    allowMultiple?: boolean;
    allowCustom?: boolean;
  };

  export type UseQuestionsOptions = {
    questions: Question[];
    toolCallId: string;
  };

  export type UseQuestionsReturn = {
    currentQuestion: Question | undefined;
    currentIndex: number;
    totalQuestions: number;
    isCurrentAnswered: boolean;
    isLastQuestion: boolean;
    goToPrevious: () => void;
    goToNext: () => void;
    singleSelections: Record<string, string>;
    multiSelections: Record<string, string[]>;
    customInputs: Record<string, string>;
    setSingleSelection: (questionId: string, optionId: string) => void;
    toggleMultiSelection: (questionId: string, optionId: string) => void;
    setCustomInput: (questionId: string, value: string) => void;
    getAnswers: () => Record<string, string | string[]>;
  };

  export type UseToolSubmitOptions = {
    toolName: string;
    toolCallId: string;
    messageId: string;
  };

  export type UseToolCancelOptions = {
    toolCallId: string;
    messageId: string;
  };

  export type UseToolSubmitReturn<T = unknown> = {
    submit: (output: T) => void;
    canSubmit: boolean;
  };

  export type UseToolCancelReturn = {
    cancel: () => void;
    canCancel: boolean;
  };

  export type DeferredTool = AgentSession.Message["parts"][number];

  export type PendingDeferredToolQueueItem = {
    part: DeferredTool;
    isActive: boolean;
    isPersisted: boolean;
    queuePosition: number;
    queueTotal: number;
    upNextPart?: DeferredTool;
  };

  export type DeferredToolPresenter = {
    isDeferredToolPart: (part: DeferredTool) => boolean;
    getDeferredToolLabel?: (part: DeferredTool | undefined) => string | undefined;
  };

  export type PendingActionRenderInfo = {
    messageId: string;
    isActive: boolean;
    isPersisted: boolean;
    queuePosition: number;
    queueTotal: number;
    label?: string;
    upNextPart?: DeferredTool;
    upNextLabel?: string;
  };

  export type PendingActionRenderEntry = {
    key: string;
    part: DeferredTool;
    info: PendingActionRenderInfo & { isInteractive: boolean };
  };

  export type BuildPendingDeferredActionStateOptions = {
    pendingParts: DeferredTool[];
    hasPendingActions: boolean;
    isStreaming: boolean;
    deferredToolPresenter: DeferredToolPresenter;
    lastAssistantMessageId?: string;
    isToolPartPersisted: (messageId: string, toolCallId: string) => boolean;
  };

  export type PendingDeferredActionState = {
    pendingDeferredToolQueue: PendingDeferredToolQueueItem[];
    hasRenderablePendingActions: boolean;
    showPendingFallback: boolean;
    showStop: boolean;
  };

  const questionsStateSchema = z.object({
    currentIndex: z.number(),
    singleSelections: z.record(z.string(), z.string()),
    multiSelections: z.record(z.string(), z.array(z.string())),
    customInputs: z.record(z.string(), z.string()),
  });

  type QuestionsState = z.infer<typeof questionsStateSchema>;

  const defaultQuestionsState = (): QuestionsState => ({
    currentIndex: 0,
    singleSelections: {},
    multiSelections: {},
    customInputs: {},
  });

  export function useQuestions({ questions, toolCallId }: UseQuestionsOptions): UseQuestionsReturn {
    const { userId } = AgentChat.useIds();
    const draftKey = `agentTools.questions.${toolCallId}`;

    const { state, set } = DraftUI.useDraft(
      userId,
      draftKey,
      questionsStateSchema,
      defaultQuestionsState,
    );

    const { currentIndex, singleSelections, multiSelections, customInputs } = state;
    const totalQuestions = questions.length;
    const currentQuestion = questions[currentIndex];
    const isLastQuestion = currentIndex === totalQuestions - 1;

    const isCurrentAnswered = useMemo(() => {
      if (!currentQuestion) return false;

      if (currentQuestion.allowMultiple) {
        const selected = multiSelections[currentQuestion.id] ?? [];
        if (selected.includes(CUSTOM_OPTION_ID)) {
          return (customInputs[currentQuestion.id] ?? "").trim().length > 0;
        }
        return selected.length > 0;
      }

      const selected = singleSelections[currentQuestion.id];
      if (!selected) return false;
      if (selected === CUSTOM_OPTION_ID) {
        return (customInputs[currentQuestion.id] ?? "").trim().length > 0;
      }
      return true;
    }, [currentQuestion, singleSelections, multiSelections, customInputs]);

    const goToPrevious = useCallback(() => {
      set({ ...state, currentIndex: Math.max(0, currentIndex - 1) });
    }, [set, state, currentIndex]);

    const goToNext = useCallback(() => {
      set({ ...state, currentIndex: Math.min(totalQuestions - 1, currentIndex + 1) });
    }, [set, state, currentIndex, totalQuestions]);

    const setSingleSelection = useCallback(
      (questionId: string, optionId: string) => {
        set({
          ...state,
          singleSelections: { ...singleSelections, [questionId]: optionId },
        });
      },
      [set, state, singleSelections],
    );

    const toggleMultiSelection = useCallback(
      (questionId: string, optionId: string) => {
        const current = multiSelections[questionId] ?? [];
        const newSelection = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
        set({
          ...state,
          multiSelections: { ...multiSelections, [questionId]: newSelection },
        });
      },
      [set, state, multiSelections],
    );

    const setCustomInput = useCallback(
      (questionId: string, value: string) => {
        set({
          ...state,
          customInputs: { ...customInputs, [questionId]: value },
        });
      },
      [set, state, customInputs],
    );

    const getAnswers = useCallback((): Record<string, string | string[]> => {
      const answers: Record<string, string | string[]> = {};

      for (const question of questions) {
        if (question.allowMultiple) {
          const selected = multiSelections[question.id] ?? [];
          answers[question.id] = selected.map((optionId) =>
            optionId === CUSTOM_OPTION_ID ? (customInputs[question.id] ?? "") : optionId,
          );
        } else {
          const selected = singleSelections[question.id];
          if (selected === CUSTOM_OPTION_ID) {
            answers[question.id] = customInputs[question.id] ?? "";
          } else {
            answers[question.id] = selected ?? "";
          }
        }
      }

      return answers;
    }, [questions, singleSelections, multiSelections, customInputs]);

    return useMemo(
      () => ({
        currentQuestion,
        currentIndex,
        totalQuestions,
        isCurrentAnswered,
        isLastQuestion,
        goToPrevious,
        goToNext,
        singleSelections,
        multiSelections,
        customInputs,
        setSingleSelection,
        toggleMultiSelection,
        setCustomInput,
        getAnswers,
      }),
      [
        currentQuestion,
        currentIndex,
        totalQuestions,
        isCurrentAnswered,
        isLastQuestion,
        goToPrevious,
        goToNext,
        singleSelections,
        multiSelections,
        customInputs,
        setSingleSelection,
        toggleMultiSelection,
        setCustomInput,
        getAnswers,
      ],
    );
  }

  export function useToolSubmit<T = unknown>({
    toolName,
    toolCallId,
    messageId,
  }: UseToolSubmitOptions): UseToolSubmitReturn<T> {
    const { agentType, sessionId, canEditSession } = AgentChat.useIds();
    const zero = useZero();
    const canSubmit = canEditSession;

    const submit = useCallback(
      (output: T) => {
        if (!canSubmit) {
          return;
        }

        zero.mutate(
          mutators.agentSession.addToolCallOutput({
            agentType,
            sessionId,
            toolName,
            toolCallId,
            toolOutput: output,
            messageId,
          }),
        );
      },
      [zero, agentType, sessionId, toolName, toolCallId, messageId, canSubmit],
    );

    return useMemo(() => ({ submit, canSubmit }), [submit, canSubmit]);
  }

  export function useToolCancel({
    toolCallId,
    messageId,
  }: UseToolCancelOptions): UseToolCancelReturn {
    const { agentType, sessionId, canEditSession } = AgentChat.useIds();
    const zero = useZero();
    const canCancel = canEditSession;

    const cancel = useCallback(() => {
      if (!canCancel) {
        return;
      }

      zero.mutate(
        mutators.agentSession.cancelToolCall({
          agentType,
          sessionId,
          messageId,
          toolCallId,
        }),
      );
    }, [zero, agentType, sessionId, messageId, toolCallId, canCancel]);

    return useMemo(() => ({ cancel, canCancel }), [cancel, canCancel]);
  }

  export function buildPendingDeferredToolQueue(
    pendingParts: DeferredTool[],
    isDeferredToolPart: (part: DeferredTool) => boolean,
    isPersistedPart: (part: DeferredTool) => boolean = () => true,
  ): PendingDeferredToolQueueItem[] {
    const pendingDeferredParts = pendingParts.filter(isDeferredToolPart);
    const queueTotal = pendingDeferredParts.length;

    return pendingDeferredParts.map((part, index) => ({
      part,
      isActive: index === 0,
      isPersisted: isPersistedPart(part),
      queuePosition: index + 1,
      queueTotal,
      upNextPart: pendingDeferredParts[index + 1],
    }));
  }

  export function shouldRenderPendingActionsFallback(
    hasPendingActions: boolean,
    pendingDeferredToolQueue: readonly PendingDeferredToolQueueItem[],
  ): boolean {
    return hasPendingActions && pendingDeferredToolQueue.length === 0;
  }

  export function buildPendingDeferredActionState({
    pendingParts,
    hasPendingActions,
    isStreaming,
    deferredToolPresenter,
    lastAssistantMessageId,
    isToolPartPersisted,
  }: BuildPendingDeferredActionStateOptions): PendingDeferredActionState {
    const pendingDeferredToolQueue = buildPendingDeferredToolQueue(
      pendingParts,
      deferredToolPresenter.isDeferredToolPart,
      (part) => {
        if (!lastAssistantMessageId || !("toolCallId" in part)) {
          return false;
        }
        return isToolPartPersisted(lastAssistantMessageId, part.toolCallId);
      },
    );

    return {
      pendingDeferredToolQueue,
      hasRenderablePendingActions: hasPendingActions && pendingDeferredToolQueue.length > 0,
      showPendingFallback: shouldRenderPendingActionsFallback(
        hasPendingActions,
        pendingDeferredToolQueue,
      ),
      showStop: isStreaming,
    };
  }

  export function buildPendingActionRenderList(
    pendingDeferredToolQueue: readonly PendingDeferredToolQueueItem[],
    lastAssistantMessageId: string,
    deferredToolPresenter?: DeferredToolPresenter,
  ): PendingActionRenderEntry[] {
    const getDeferredToolLabel = deferredToolPresenter?.getDeferredToolLabel;

    return pendingDeferredToolQueue.map(
      ({ part, isActive, isPersisted, queuePosition, queueTotal, upNextPart }) => ({
        key:
          "toolCallId" in part
            ? `pending-${lastAssistantMessageId}-${part.toolCallId}`
            : `pending-${lastAssistantMessageId}-${queuePosition}`,
        part,
        info: {
          messageId: lastAssistantMessageId,
          isActive,
          isPersisted,
          isInteractive: isActive && isPersisted,
          queuePosition,
          queueTotal,
          label: getDeferredToolLabel?.(part),
          upNextPart,
          upNextLabel: getDeferredToolLabel?.(upNextPart),
        },
      }),
    );
  }

  export function isOutputCanceledState(part: { state?: string } | undefined): boolean {
    return part?.state === "output-canceled";
  }

  export function formatQuestionAnswer(
    question: Question,
    answer: string | string[] | undefined,
  ): string {
    const getOptionLabel = (optionId: string) => {
      const option = question.options.find((candidate) => candidate.id === optionId);
      return option?.label ?? optionId;
    };

    const formattedAnswer = Array.isArray(answer)
      ? answer.map(getOptionLabel).join(", ")
      : getOptionLabel(answer ?? "");

    return formattedAnswer || "No answer";
  }

  export function formatPendingQueueProgress(
    queuePosition: number,
    queueTotal: number,
  ): string | undefined {
    if (queueTotal <= 1) {
      return undefined;
    }

    return `${queuePosition} of ${queueTotal}`;
  }

  export function getToolErrorDisplayText(errorText?: string): string {
    if (errorText?.includes("Invalid input for tool")) {
      return "The assistant made a formatting error. It will try again.";
    }

    return errorText || "Something went wrong...";
  }
}

export namespace AskQuestions {
  export const toolName = "askQuestions";

  export type Part = AgentTools.DeferredTool & {
    type: "tool-askQuestions";
    toolCallId: string;
    input?: unknown;
    output?: unknown;
  };

  type CompletedPart = Part & { state: "output-available" };

  export function isPart(part: { type: string }): part is Part {
    return part.type === "tool-askQuestions";
  }

  export function isCompletedPart(part: { type: string; state?: string }): part is CompletedPart {
    return part.type === "tool-askQuestions" && part.state === "output-available";
  }
}
